/**
 * Stdlib export index — a lazily-built, cached catalogue of every type a
 * bundled `arch.*` package EXPORTS, so completion can offer a stdlib kind
 * (`service`) in a fresh file that hasn't `use`d its package yet and wire up
 * the missing `use … from …` on accept (auto-import, P3).
 *
 * The workspace loads a stdlib package only when it's already referenced
 * (`package-loader.ts` `collectReferencedStdlib`), so `workspace.exportedTypes()`
 * is empty in a fresh file — exactly the flagship authoring moment. This index
 * fills the gap WITHOUT eagerly resolving `arch.cloud.*`: it PARSE-scans each
 * package's `.arch` files (lex + parse, never `resolvePackage`) once per session
 * and records every `export type <parent> <name>` declaration.
 *
 * All IO goes through {@link LspIO}, so it is identical across the node FS and
 * the browser VFS. If `stdlibUri` doesn't resolve (STDLIB_NOT_FOUND), the walk
 * degrades to an empty index — no throw.
 */

import type { LspIO } from "../io.js";
import { joinDescriptions, parse, parsePackageManifest } from "@archlang/engine";

export interface StdlibExport {
  /** The exported type's name — the kind an author writes (`service`). */
  readonly kindName: string;
  /** The dotted package that exports it (`arch.backend`) — the `use … from` target. */
  readonly packageName: string;
  /**
   * The ROOT base kind this type ultimately subtypes: `module` / `surface` /
   * `interface` / `process` / `view`. Resolved by walking the `parentType`
   * chain across the whole stdlib (`postgres → relational → database → module`)
   * so scope-legality (a surface body admits no module kinds) can be honored
   * without a full resolve. Defaults to `module` when a parent isn't an export.
   */
  readonly rootBase: string;
  /** First description line, if any — lazily rendered into the item's docs. */
  readonly doc?: string;
}

const BASE_KINDS: ReadonlySet<string> = new Set([
  "module",
  "surface",
  "interface",
  "process",
  "view",
]);

const PRUNE = new Set(["node_modules", ".git", "dist"]);

/** Hard cap on directories walked — a pathological stdlib layout can't hang init. */
const MAX_DIRS = 5000;

function stripTrailingSlash(uri: string): string {
  return uri.endsWith("/") ? uri.slice(0, -1) : uri;
}

/**
 * Walk the stdlib tree under `stdlibUri` and index every exported type. A file
 * is attributed to the nearest enclosing `package:` manifest, so nested spaces
 * inside a package share its package name. Missing/empty stdlib → `[]`.
 */
export async function buildStdlibExportIndex(
  io: LspIO,
  stdlibUri: string
): Promise<StdlibExport[]> {
  const raw: {
    kindName: string;
    packageName: string;
    parentType: string;
    doc?: string;
  }[] = [];
  // Global name → immediate parent, for root-base resolution across packages.
  const parentOf = new Map<string, string>();

  const stack: { dirUri: string; pkg: string | undefined }[] = [
    { dirUri: stripTrailingSlash(stdlibUri), pkg: undefined },
  ];
  let dirs = 0;
  while (stack.length > 0) {
    if (dirs++ > MAX_DIRS) break;
    const { dirUri, pkg } = stack.pop()!;
    let entries: readonly { name: string; kind: "file" | "dir" }[];
    try {
      entries = await io.readDirectory(dirUri);
    } catch {
      // Directory doesn't exist / isn't readable (e.g. STDLIB_NOT_FOUND at the
      // root). Skip it — the index simply omits its exports.
      continue;
    }

    // Descend the package context: a `package:` manifest here names every
    // `.arch` beneath it (until a deeper `package:` manifest overrides).
    let currentPkg = pkg;
    if (entries.some((e) => e.name === "package.archspace" && e.kind === "file")) {
      const mUri = io.joinUri(dirUri, "package.archspace");
      const text = await io.readFile(mUri);
      const m = text !== null ? parsePackageManifest(text, mUri).manifest : undefined;
      if (m?.package !== undefined) currentPkg = m.package.join(".");
    }

    for (const entry of entries) {
      if (PRUNE.has(entry.name)) continue;
      if (entry.kind === "dir") {
        stack.push({
          dirUri: stripTrailingSlash(io.joinUri(dirUri, entry.name + "/")),
          pkg: currentPkg,
        });
      } else if (entry.name.endsWith(".arch") && currentPkg) {
        const fileUri = io.joinUri(dirUri, entry.name);
        const text = await io.readFile(fileUri);
        if (text === null) continue;
        let parsed;
        try {
          parsed = parse(text, fileUri);
        } catch {
          continue;
        }
        for (const decl of parsed.file.declarations) {
          if (decl.kind !== "type") continue;
          const t = decl.decl;
          // Record the parent link for EVERY type (topology) — an exported type
          // may extend a NON-exported intermediate base, and `resolveRootBase`
          // needs the full chain to reach the builtin root. Only exported types
          // are OFFERED (the `raw` push), but all contribute to `parentOf`.
          parentOf.set(t.name, t.parentType);
          if (!t.exported) continue;
          const doc = joinDescriptions(t.body.descriptions);
          raw.push({
            kindName: t.name,
            packageName: currentPkg,
            parentType: t.parentType,
            doc: doc.length > 0 ? doc : undefined,
          });
        }
      }
    }
  }

  return raw.map((r) => ({
    kindName: r.kindName,
    packageName: r.packageName,
    rootBase: resolveRootBase(r.parentType, parentOf),
    doc: r.doc,
  }));
}

/** Walk `parentType` up the export chain until a builtin base kind; default to
 *  `module` when a link is missing or a cycle is hit (best-effort scope hint). */
function resolveRootBase(parentType: string, parentOf: Map<string, string>): string {
  let cur = parentType;
  const seen = new Set<string>();
  while (!BASE_KINDS.has(cur)) {
    if (seen.has(cur)) return "module";
    seen.add(cur);
    const next = parentOf.get(cur);
    if (next === undefined) return "module";
    cur = next;
  }
  return cur;
}
